import { getClientFeedUrl } from '~/services/request/index.ts';
import { Player } from '@brightcove/web-sdk/ui';
import { useEffect, useCallback } from 'react';
import { requestJsonData } from '~/utils/request-json-data';

const DEFAULT_OVERLAY_DURATION = 30;

const usePlayNext = (
  player: Player | null,
  playNext: (event: any) => void,
  playNextFeedUrl: string | null,
  setIsRunningPlayNext: any,
  setPlayNextFeed: any,
  setDuration: any,
  setCurrentPosition: any
) => {
  const fetchPlayNextFeed = useCallback(async () => {
    if (!playNextFeedUrl) return;

    const clientFeedUrl: string = getClientFeedUrl(playNextFeedUrl);

    const feed = await requestJsonData({
      url: clientFeedUrl,
      sessionStorageKey: `play-next-feed-${playNextFeedUrl}`,
    });

    setPlayNextFeed(feed);
  }, [playNextFeedUrl, setPlayNextFeed]);

  const showPlayNextOverlay = useCallback(() => {
    if (!player) return;

    const duration = player.getDuration() || 0;
    const currentPosition = player.getCurrentTime();

    setDuration(duration);
    setCurrentPosition(currentPosition || 0);

    const triggerTime = Math.abs(duration - DEFAULT_OVERLAY_DURATION) - 1;

    if (!currentPosition || !triggerTime) {
      return;
    }

    if (currentPosition <= triggerTime) {
      setIsRunningPlayNext(false);
    }

    if (currentPosition > triggerTime) {
      setIsRunningPlayNext(true);
    }
  }, [player, setDuration, setCurrentPosition, setIsRunningPlayNext]);

  useEffect(() => {
    if (!player || !playNextFeedUrl) return;
    player.addEventListener(
      Player.Event.PlayerTimeChanged,
      showPlayNextOverlay
    );
    player.addEventListener(
      Player.Event.PlayerLoadedMetadata,
      fetchPlayNextFeed
    );
    player.addEventListener(Player.Event.PlayerEnded, playNext);

    return () => {
      player.removeEventListener(
        Player.Event.PlayerTimeChanged,
        showPlayNextOverlay
      );
      player.removeEventListener(
        Player.Event.PlayerLoadedMetadata,
        fetchPlayNextFeed
      );
      player.removeEventListener(Player.Event.PlayerEnded, playNext);
    };
  }, [
    player,
    playNextFeedUrl,
    showPlayNextOverlay,
    fetchPlayNextFeed,
    playNext,
  ]);
};

export default usePlayNext;
